import React, { useState } from "react";
import { IconButton, Message, useToaster } from "rsuite";
import EditIcon from "@rsuite/icons/Edit";
import axiosInstance from "../../../../../../appHelpers/axiosInstance";
import { useAuth0 } from "@auth0/auth0-react";
import Modal from "../../../../../ui/Modal/Modal";
import Button from "../../../../../ui/Button/Button";
import Input from "../../../../../ui/Input/Input";
import styled from "styled-components";
import { colors } from "../../../../../ui/theme/colors";

export const EditSiteGroup = ({ group, update, setUpdate }) => {
  const { getAccessTokenSilently } = useAuth0();
  const toaster = useToaster();
  const initFormGroup = {
    name: group.name || "",
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setFormValue(initFormGroup);
  };

  const [open, setOpen] = useState(false);
  const [formValue, setFormValue] = useState(initFormGroup);
  const [loading, setLoading] = useState(false);

  const successMessage = (
    <Message showIcon type="success" header="Success">
      Site group "{formValue.name}" was updated successfully.
    </Message>
  );

  const errorMessage = (
    <Message showIcon type="error" header="Error">
      Failed to update site group. Please try again.
    </Message>
  );

  const onSubmit = async () => {
    if (!formValue.name || formValue.name.trim() === "") {
      toaster.push(
        <Message showIcon type="warning" header="Warning">
          Please enter a group name.
        </Message>,
        "topCenter",
      );
      return;
    }

    setLoading(true);
    try {
      const token = await getAccessTokenSilently({
        audience: `${process.env.REACT_APP_AUTH0_EXPRESS_API}`,
      });

      const response = await axiosInstance({
        method: "PUT",
        url: `/customer/site-groups/${group.id}`,
        headers: {
          "Content-Type": "application/json",
          authorization: `Bearer ${token}`,
        },
        data: JSON.stringify({
          name: formValue.name.trim(),
        }),
      });

      setUpdate(!update);
      toaster.push(successMessage, "topCenter");
      handleClose();
    } catch (error) {
      console.error("Error updating site group:", error);
      toaster.push(errorMessage, "topCenter");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <IconButton
        icon={<EditIcon />}
        onClick={handleOpen}
        size="sm"
        appearance="subtle"
      />

      <Modal
        open={open}
        onClose={handleClose}
        title={`Edit Site Group: ${group.name}`}
        size="sm"
        showFooter={true}
        footerActions={
          <StyledFooterActions>
            <Button variant="subtle" onClick={handleClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={onSubmit}
              loading={loading}
              disabled={!formValue.name || formValue.name.trim() === ""}
            >
              Update Group
            </Button>
          </StyledFooterActions>
        }
      >
        <ModalContent>
          <FormGroup>
            <Label>Group Name *</Label>
            <Input
              value={formValue.name}
              onChange={(value) => setFormValue({ ...formValue, name: value })}
              placeholder="Enter group name..."
              autoFocus
            />
            <HelpText>Enter a descriptive name for this site group.</HelpText>
          </FormGroup>

          <GroupInfo>
            <InfoLabel>Current Group Information</InfoLabel>
            <InfoItem>
              <InfoKey>Original Name:</InfoKey>
              <InfoValue>{group.name}</InfoValue>
            </InfoItem>
            {group.updated_at && (
              <InfoItem>
                <InfoKey>Last Modified:</InfoKey>
                <InfoValue>
                  {new Date(group.updated_at).toLocaleString()}
                </InfoValue>
              </InfoItem>
            )}
          </GroupInfo>
        </ModalContent>
      </Modal>
    </>
  );
};

const ModalContent = styled.div`
  padding: 24px;
`;

const StyledFooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 24px;
`;

const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral[700]};
`;

const HelpText = styled.div`
  font-size: 12px;
  color: ${colors.neutral[500]};
  line-height: 1.4;
  margin-top: 4px;
`;

const GroupInfo = styled.div`
  background: ${colors.neutral[50]};
  border: 1px solid ${colors.neutral[200]};
  border-radius: 8px;
  padding: 16px;
`;

const InfoLabel = styled.h4`
  margin: 0 0 12px 0;
  font-size: 14px;
  font-weight: 600;
  color: ${colors.neutral[700]};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const InfoItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid ${colors.neutral[200]};

  &:last-child {
    border-bottom: none;
  }
`;

const InfoKey = styled.span`
  font-size: 13px;
  color: ${colors.neutral[600]};
  font-weight: 500;
`;

const InfoValue = styled.span`
  font-size: 13px;
  color: ${colors.neutral[800]};
  font-weight: 600;
  text-align: right;
  max-width: 60%;
  word-break: break-word;
`;
